import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { RecentWeek } from '@/types';
import { weekDate } from '@/config/season';

// Week-by-week league lines for one league. Collapsed = the latest night only
// (the line the Fall Watch cards always showed); "All N weeks" opens the full
// season, newest first. Games of 200+ and the best series get the accent color.
// `tone` matches the surface: Home cards are dark, profile cards are white.
export const WeekLines: React.FC<{
  weeks: RecentWeek[];
  tone?: 'dark' | 'light';
  defaultOpen?: boolean;
  className?: string;
}> = ({ weeks, tone = 'dark', defaultOpen = false, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  const bowled = weeks.filter((w) => w.scores.length > 0);
  if (!bowled.length) return null;
  const latest = bowled[bowled.length - 1];
  const bestSeries = Math.max(...bowled.map((w) => w.series));
  const dark = tone === 'dark';
  const c = {
    rule: dark ? 'border-willard-grey-800' : 'border-willard-grey-100',
    label: 'text-willard-grey-500',
    text: dark ? 'text-willard-grey-300' : 'text-willard-grey-700',
    accent: dark ? 'text-willard-taupe' : 'text-willard-black',
    hot: dark ? 'text-willard-taupe' : 'text-willard-black font-black',
    row: dark ? 'border-willard-grey-800/60' : 'border-willard-grey-100',
    button: dark ? 'text-willard-grey-500 hover:text-willard-taupe' : 'text-willard-grey-500 hover:text-willard-black',
  };
  const Line: React.FC<{ w: RecentWeek; bold?: boolean }> = ({ w, bold }) => (
    <>
      <span className={`uppercase tracking-widest ${c.label} shrink-0`}>
        {w.week ? `Wk ${w.week}` : 'Latest'}
        {w.date ? ` · ${weekDate(w.date)}` : ''}
      </span>
      <span className={`${bold ? 'font-bold' : 'font-semibold'} ${c.text} tabular-nums text-right`}>
        {w.scores.map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && ' · '}
            <span className={s >= 200 ? c.hot : ''}>{s}</span>
          </React.Fragment>
        ))}{' '}
        <span className={w.series === bestSeries && bowled.length > 1 ? c.hot : c.accent}>= {w.series}</span>
      </span>
    </>
  );

  return (
    <div className={`mt-4 pt-3 border-t ${c.rule} text-xs ${className}`}>
      {!open && (
        <div className="flex items-center justify-between gap-3">
          <Line w={latest} bold />
        </div>
      )}
      {open && (
        <ul className="space-y-1.5">
          {[...bowled].reverse().map((w, i) => (
            <li key={`${w.week ?? 'x'}-${w.date ?? i}`} className={`flex items-center justify-between gap-3 ${i > 0 ? `pt-1.5 border-t ${c.row}` : ''}`}>
              <Line w={w} bold={i === 0} />
            </li>
          ))}
        </ul>
      )}
      {bowled.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          aria-expanded={open}
          className={`mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold ${c.button} transition-colors`}
        >
          {open ? 'Latest night only' : `All ${bowled.length} weeks`}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
};

export default WeekLines;
